import { useMemo, useRef, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { getApiVersion } from '@/api/version'
import {
  LayoutDashboard, BarChart3, Globe, Server,
  ChevronRight, ChevronDown, User2, LogOut,
  History,
  // Icon map — all icons the backend catalogue may reference
  MapPin, Radio, Clock, Route, Terminal,
  Bell, SlidersHorizontal, Tag,
  Truck, List, Cpu, Layers, WalletCards, RefreshCw,
  Globe2, CreditCard, BarChart2, FileText,
  Wrench, Package, Zap, Share2, Settings,
  Users, UserCheck, ShieldCheck, KeyRound,
  Briefcase, CheckSquare, Calendar, CalendarDays, Send,
  DollarSign, BookOpen, Map, AlertCircle,
  Heart, Battery, BatteryCharging, Link2, Repeat, Grid3X3,
  Building2, Receipt, Wallet, Car, School,
  PawPrint, Bus, Pentagon, Video,
  // Additional icons from backend menu catalogue
  Droplet, PieChart, Activity, UploadCloud,
  Mail, Download, Pencil, Award,
  MessageCircle, AlertTriangle, Megaphone,
} from 'lucide-react'
import { useUIStore } from '@/store/ui'
import { useAuthStore } from '@/store/auth'
import { useTenantStore } from '@/store/tenant'
import { getMenu, type MenuItem } from '@/api/menu'
import type { PermissionMap } from '@/types'
import { cn } from '@/utils/cn'

// ── Icon resolver ──────────────────────────────────────────────────────────────

const ICON_MAP: Record<string, React.ElementType> = {
  'history': History,
  'map-pin': MapPin,
  'radio': Radio,
  'clock': Clock,
  'route': Route,
  'terminal': Terminal,
  'server': Server,
  'bell': Bell,
  'sliders': SlidersHorizontal,
  'tag': Tag,
  'pentagon': Pentagon,
  'truck': Truck,
  'list': List,
  'cpu': Cpu,
  'layers': Layers,
  'sim-card': WalletCards,
  'refresh': RefreshCw,
  'globe': Globe,
  'globe2': Globe2,
  'credit-card': CreditCard,
  'bar-chart': BarChart3,
  'bar-chart-2': BarChart2,
  'file-text': FileText,
  'tool': Wrench,
  'package': Package,
  'zap': Zap,
  'share-2': Share2,
  'settings': Settings,
  'users': Users,
  'user-check': UserCheck,
  'shield': ShieldCheck,
  'shield-check': ShieldCheck,
  'key': KeyRound,
  'briefcase': Briefcase,
  'check-square': CheckSquare,
  'calendar': Calendar,
  'calendar-days': CalendarDays,
  'send': Send,
  'dollar-sign': DollarSign,
  'book': BookOpen,
  'map': Map,
  'bus': Bus,
  'alert-circle': AlertCircle,
  'heart': Heart,
  'battery': Battery,
  'battery-charging': BatteryCharging,
  'link': Link2,
  'repeat': Repeat,
  'grid': Grid3X3,
  'building': Building2,
  'receipt': Receipt,
  'wallet': Wallet,
  'car': Car,
  'school': School,
  'paw-print': PawPrint,
  'video':       Video,
  // Additional icons
  'droplet':     Droplet,
  'pie-chart':   PieChart,
  'activity':    Activity,
  'upload-cloud': UploadCloud,
  'box':         Package,
  'mail':        Mail,
  'download':    Download,
  'edit-2':      Pencil,
  'layout':      FileText,
  'award':          Award,
  'trophy':         Award,
  'message-circle': MessageCircle,
  'star':           Award,
  'alert-triangle': AlertTriangle,
  'megaphone':      Megaphone,
}

function resolveIcon(name: string): React.ElementType {
  return ICON_MAP[name] ?? Settings
}

// ── Route map: backend menu key → frontend route ───────────────────────────────

const ROUTE_MAP: Record<string, string> = {
  // Tracking
  'tracking.live':     '/map',
  'tracking.history':  '/history',
  'tracking.trips':    '/trips',
  'tracking.packets':  '/packets',
  // Dashcam
  'dashcam.live':        '/dashcam',
  'dashcam.recordings':  '/dashcam',
  'dashcam.alarm_clips': '/dashcam',
  'dashcam.config':      '/admin/dashcam-config',
  // EV
  'ev.live':              '/ev/status',
  'ev.status':            '/ev/status',
  'ev.history':           '/ev/history',
  'ev.charging_sessions': '/ev/charging-sessions',
  'ev.stations':          '/ev/stations',
  'ev.batteries':         '/ev/batteries',
  'ev.battery_assign':    '/ev/batteries',
  'ev.battery_swap':      '/ev/swaps',
  // EV legacy keys
  'batteries.list':   '/ev/batteries',
  'batteries.assign': '/ev/batteries',
  'batteries.swap':   '/ev/swaps',
  'ev_stations.manage': '/ev/stations',
  // Reports
  'reports.list':            '/reports',
  'reports.view':            '/reports',
  'reports.all':             '/reports',
  'reports.generate':        '/reports',
  'reports.trips':           '/reports',
  'reports.trip':            '/reports',
  'reports.speed':           '/reports',
  'reports.distance':        '/reports',
  'reports.mileage':         '/reports',
  'reports.fuel':            '/reports',
  'reports.utilization':     '/reports',
  'reports.behavior':        '/reports',
  'reports.ignition':        '/reports',
  'reports.geofence':        '/reports',
  'reports.geofence_events': '/reports/geofence-events',
  'reports.activity':        '/reports',
  'reports.inputs':          '/reports',
  'reports.schedules':       '/reports',
  'reports.alerts':          '/reports',
  'reports.alert':           '/reports',
  'reports.summary':         '/reports',
  'reports.manage':          '/reports',
  // Alerts
  'alerts.list':       '/alerts',
  'alerts.manage':     '/alerts',
  'alerts.types':       '/admin/alert-types',
  'alert_types.list':   '/admin/alert-types',
  'alert_types.manage': '/admin/alert-types',
  'alerts.rules':       '/admin/alert-rules',
  'admin.alert_rules':  '/admin/alert-rules',
  // Calendars
  'calendars':              '/admin/calendars',
  'calendars.list':         '/admin/calendars',
  'calendars.manage':       '/admin/calendars',
  'admin.calendars':        '/admin/calendars',
  'calendar':               '/admin/calendars',
  // Geofences
  'geofences.list':            '/geofences',
  'geofences.events':          '/geofences',
  'geofences.assign_vehicles': '/geofences',
  // Vehicles
  'vehicles.list':          '/vehicles',
  'vehicles.assign_device': '/vehicles',
  'vehicles.types':         '/admin/vehicle-types',
  // Drivers
  'drivers.list':           '/drivers',
  'drivers.assign_vehicle': '/drivers',
  // Devices
  'devices.list':  '/devices',
  'devices.types': '/devices',
  // Field Force
  'fieldforce.list':       '/field-force/employees',
  'fieldforce.tasks':      '/field-force/tasks',
  'fieldforce.attendance': '/field-force/attendance',
  // Dispatch
  'dispatch.list':     '/dispatch/bookings',
  'dispatch.services': '/dispatch/service-types',
  'dispatch.finance':  '/dispatch/bookings',
  'dispatch.packages': '/dispatch/packages',
  // School
  'school.list':          '/school/schools',
  'school.students':      '/school/students',
  'school.attendance':    '/school/attendance',
  'school.routes':        '/school/routes',
  'school.runs':          '/school/runs',
  'school.schedule':      '/school/schedule',
  'school.staff':         '/school/staff',
  'school.members':       '/school/members',
  'school.driver':        '/school/driver',
  'school.parent_portal':   '/school/parent-portal',
  'my_students':            '/school/parent-portal',
  'school.announcements':   '/school/announcements',
  // Transport
  'transport.list':      '/transport/routes',
  'transport.roster':    '/transport/rosters',
  'transport.sos':       '/transport/sos',
  'transport.vendors':   '/transport/vendors',
  'transport.employees': '/transport/employees',
  'transport.vrp':       '/transport/vrp',
  // SOS standalone module (vehicle/panic-button — issue #89)
  'sos.active':   '/sos/active',
  'sos.history':  '/sos/history',
  // E-Challans (compliance — issue #103)
  'challans.fleet':          '/compliance/challans',
  'challans.list':           '/compliance/challans',
  'challans.config':         '/admin/challans',
  'admin.challans_config':   '/admin/challans',
  // Routing
  'routing':                     '/routing',
  'routing.plans':               '/routing',
  'routing.executions':          '/routing',
  'routing.config':              '/routing',
  // Pets
  'pets.list':   '/pets',
  'pets.breeds': '/pets',
  // SIMs
  'sims.list':      '/sims',
  'sims.sync':      '/sims',
  'sims.providers': '/sims',
  // My Billing (owner self-service)
  'my_billing.subscription': '/billing/my-subscription',
  'my_billing.invoices':     '/billing/my-billing',
  'my_billing.payments':     '/billing/my-billing',
  // Billing — admin / domain-admin keys
  'billing.dashboard':           '/billing/dashboard',
  'billing.platform_dashboard':  '/billing/dashboard',
  'billing.overview':            '/billing/subscriptions',
  'billing.subscriptions':       '/billing/subscriptions',
  'billing.manage_subscription': '/billing/subscriptions',
  'billing.invoices':            '/billing/invoices',
  'billing.manage_invoices':     '/billing/invoices',
  'billing.payments':            '/billing/payments',
  'billing.manage_payments':     '/billing/payments',
  'billing.statement':           '/billing/payments',
  'billing.wallet':              '/billing/payments',
  'billing.manage_wallet':       '/billing/payments',
  'billing.renewals':            '/billing/renewals',
  'billing.overdue':             '/billing/overdue',
  'billing.exports':             '/billing/tax-config',
  'billing.dunning_rules':       '/billing/dunning-rules',
  'billing.customers':           '/billing/customers',
  'billing.manage_customers':    '/billing/customers',
  'billing.entities':            '/billing/subscriptions',
  'billing.manage_entities':     '/billing/subscriptions',
  'billing.plans':               '/billing/domain-plans',
  'billing.domain_plans':        '/billing/domain-plans',
  'billing.manage_domain_plans': '/billing/domain-plans',
  'billing.platform_plans':      '/billing/plans',
  'billing.platform_plan':       '/billing/platform-plan',
  'billing.gateways':            '/billing/gateways',
  'billing.manage_gateways':     '/billing/gateways',
  'billing.profiles':            '/billing/profile',
  'billing.profile':             '/billing/profile',
  'billing.manage_profiles':     '/billing/profile',
  'billing.tax_config':          '/billing/tax-config',
  'billing.pdf_templates':       '/billing/pdf-templates',
  'billing.pdf-templates':       '/billing/pdf-templates',
  'billing.signing_cert':        '/billing/signing-cert',
  'billing.subscription':        '/billing/my-subscription',
  'billing.my_billing':          '/billing/my-billing',
  'billing.platform_profile':        '/billing/platform-profile',
  'billing.platform_gateways':       '/billing/platform-gateways',
  'billing.platform_pdf_templates':  '/billing/pdf-templates',
  // Maintenance
  'maintenance.records':   '/maintenance/records',
  'maintenance.schedules': '/maintenance/schedules',
  'maintenance.view':      '/maintenance/records',
  'maintenance.manage':    '/maintenance/records',
  // Technician
  'technician.stock':    '/technician/stock',
  'technician.activate': '/technician/activate',
  'technician.handover': '/technician/handover',
  // Admin
  'admin.users':                  '/admin/users',
  'admin.manage_users':           '/admin/users',
  'admin.user_list':              '/admin/users',
  'admin.list_users':             '/admin/users',
  'admin.user_types':             '/admin/user-types',
  'admin.permissions':            '/admin/user-types',
  'admin.manage_user_types':      '/admin/user-types',
  'admin.bulk':                   '/admin/bulk-import',
  'admin.onboarding':             '/admin/onboarding',
  'admin.notification_channels':  '/admin/notifications',
  'admin.scoring_config':         '/scoring',
  'admin.data_brokers':           '/admin/data-brokers',
  'admin.api_keys':               '/admin/api-keys',
  'admin.domains':                '/white-label/domains',
  'admin.migrations':             '/white-label/migrations',
  'whitelabel.migrations':        '/white-label/migrations',
  'admin.simulate':               '/white-label/simulate',
  'whitelabel.simulate':          '/white-label/simulate',
  'admin.dashcam_config':         '/admin/dashcam-config',
  'admin.doc_reminders':          '/admin/document-reminders',
  'admin.audit_log':              '/admin/audit-log',
  'audit.view':                   '/admin/audit-log',
  'audit_log.list':               '/admin/audit-log',
  'admin.delegations':            '/admin/delegations',
  'admin.announcements':          '/admin/announcements',
  'announcements.send':           '/admin/announcements',
  'announcements':                '/admin/announcements',
  // Integrations
  'integrations.databroker':     '/admin/data-brokers',
  // Settings
  'settings.me':                 '/settings/me',
  'settings.domain':             '/settings/domain',
  'settings.notifications':      '/admin/notifications',
  'settings.api_keys':           '/admin/api-keys',
  // System (super admin)
  'system.services':            '/system',
  'system.parsers':             '/system/parsers',
  'system.parser_connections':  '/system/parser-connections',
  // Scoring
  'scoring.leaderboard': '/scoring',
  'scoring.config':      '/scoring',
  'scoring.view':        '/scoring',
  'scoring.list':        '/scoring',
  // Alert Rules — extra key formats the backend may send
  'alert_rules':            '/admin/alert-rules',
  'alert_rule':             '/admin/alert-rules',
  // AI Assistant
  'assistant':                   '/assistant',
  'assistant.chat':              '/assistant',
  'admin.assistant_config':      '/admin/assistant-config',
  'assistant.config':            '/admin/assistant-config',
  // Haul Cycles — backend keys (haul.*) + legacy aliases
  'haul.live':          '/haul-cycles',
  'haul.cycles':        '/haul-cycles',
  'haul.report':        '/haul-cycles',
  'haul.zones':         '/haul-cycles',
  'haulcycles.list':    '/haul-cycles',
  'haulcycles.manage':  '/haul-cycles',
  'haul_cycles.list':   '/haul-cycles',
  'haul_cycles.manage': '/haul-cycles',
  'haul_zones.manage':  '/haul-cycles',
}



// ── Post-process menu from /menu API ──────────────────────────────────────────
// The API returns a fully-filtered tree per user — no client-side permission
// filtering needed. This function:
//   1. Applies display label overrides (UX labels differ from API labels)
//   2. Moves tracking.history + tracking.trips into the Reports group
//   3. Strips payment/wallet/statement keys from billing group (shown as tabs in Customer Billing)
//   4. Cross-group route deduplication — the first group to claim a route wins;
//      later groups lose items that map to already-claimed routes.
function postProcessMenu(groups: MenuItem[], isSuperAdmin = false, perms: PermissionMap = {}): MenuItem[] {
  // Only move trips to reports group; history is already a tab in Reports page
  const MOVE_TO_REPORTS = new Set(['tracking.trips'])
  // Remove history from tracking sidebar — it's a tab inside Reports
  const HIDE_FROM_SIDEBAR = new Set(['tracking.history'])

  // Collect items to promote to Reports
  const promotedItems: MenuItem[] = []
  groups.forEach((g) => {
    if (g.key === 'tracking') {
      ;(g.children ?? []).forEach((c) => {
        if (MOVE_TO_REPORTS.has(c.key)) promotedItems.push(c)
      })
    }
  })

  // Label overrides are now handled by translateMenuKey via nav.json i18n.
  // Only special case: settings.domain shows "My Settings" for non-admin users.
  const applyOverride = (c: MenuItem): MenuItem => ({
    ...c,
    label: c.label,
  })

  // Pass 1: label overrides + tracking→reports promotion
  const pass1 = groups.map((group) => {
    if (group.key === 'tracking') {
      return {
        ...group,
        children: (group.children ?? [])
          .filter((c) => !MOVE_TO_REPORTS.has(c.key) && !HIDE_FROM_SIDEBAR.has(c.key))
          .map(applyOverride),
      }
    }

    if (group.key === 'reports') {
      return {
        ...group,
        children: [
          ...promotedItems.map(applyOverride),
          ...(group.children ?? []).map((c) => {
            return applyOverride(c)
          }),
        ],
      }
    }

    return {
      ...group,
      children: (group.children ?? []).map(applyOverride),
    }
  })

  // Pass 2: safety-guard strip for billing group.
  // Owner users now receive my_billing (not billing) per backend fix #444, so no
  // payment/wallet/statement stripping is needed for the billing group.
  // Only remaining guards: super-admin-only platform keys and non-super-only keys.
  const SUPER_ADMIN_ONLY_BILLING_KEYS = new Set([
    'billing.platform_plans',
    'billing.platform_gateways',
    'billing.platform_profile',
    'billing.platform_dashboard',
    // Super admin sees "Platform PDF Templates" — domain admin sees "Invoice Templates" instead
    'billing.platform_pdf_templates',
  ])
  // Keys only non-super admins should see (hidden from super admin to avoid duplicate)
  const NON_SUPER_ONLY_BILLING_KEYS = new Set([
    'billing.pdf_templates',
    'billing.pdf-templates',
  ])
  const pass2 = pass1.map((group) => {
    if (group.key !== 'billing') return group

    const filtered = (group.children ?? []).filter((c) => {
      if (!isSuperAdmin && SUPER_ADMIN_ONLY_BILLING_KEYS.has(c.key)) return false
      if (isSuperAdmin && NON_SUPER_ONLY_BILLING_KEYS.has(c.key)) return false
      return true
    })

    return { ...group, children: filtered }
  })

  // Pass 3: manage audit_log visibility in the admin group.
  // The backend may return admin.audit_log for all admin users regardless of
  // explicit permission, so we both FILTER and INJECT here.
  const pass3 = pass2.map((group) => {
    if (group.key === 'admin') {
      const canAuditLog =
        isSuperAdmin ||
        perms['audit_log']?.canRead === true ||
        perms['admin.audit_log']?.canRead === true

      // Remove if present but user has no audit_log permission
      const filtered = (group.children ?? []).filter(c =>
        c.key !== 'admin.audit_log' || canAuditLog
      )

      // Inject if missing and user has audit_log permission
      const alreadyPresent = filtered.some(c => c.key === 'admin.audit_log')
      const extras: MenuItem[] = (!alreadyPresent && canAuditLog)
        ? [{ key: 'admin.audit_log', label: 'Audit Log', icon: 'clipboard-list' }]
        : []

      // Inject Migrations for super admin if the backend hasn't added it yet (issue #544)
      const hasMigrations = filtered.some(c => c.key === 'admin.migrations')
      if (isSuperAdmin && !hasMigrations) {
        extras.push({ key: 'admin.migrations', label: 'Migrations', icon: 'database' })
      }

      // Inject Simulate for super admin
      const hasSimulate = filtered.some(c => c.key === 'admin.simulate' || c.key === 'whitelabel.simulate')
      if (isSuperAdmin && !hasSimulate) {
        extras.push({ key: 'admin.simulate', label: 'Simulate', icon: 'play-circle' })
      }

      const changed = filtered.length !== (group.children ?? []).length || extras.length
      return changed ? { ...group, children: [...filtered, ...extras] } : group
    }

    return group
  })

  // Pass 3b: inject billing items that the backend /menu API may omit.
  // - Super admin: needs billing.platform_plans to manage platform-tier plan definitions.
  // - Domain admin: needs billing.platform_plan to view their own platform subscription.
  const pass3b = pass3.map((group) => {
    if (group.key !== 'billing') return group

    const children = group.children ?? []

    // Super admin: inject "Platform Plans" after dashboard if missing
    if (isSuperAdmin) {
      const hasPlatformPlans = children.some(c => c.key === 'billing.platform_plans')
      if (!hasPlatformPlans) {
        const dashIdx = children.findIndex(c => c.key === 'billing.platform_dashboard' || c.key === 'billing.dashboard')
        const insertAt = dashIdx >= 0 ? dashIdx + 1 : 0
        const injected = [
          ...children.slice(0, insertAt),
          { key: 'billing.platform_plans', label: 'Platform Plans', icon: 'layers' } as MenuItem,
          ...children.slice(insertAt),
        ]
        return { ...group, children: injected }
      }
    }

    // Domain admin: inject "Platform Plan" (own subscription view) before gateways if missing
    if (!isSuperAdmin) {
      const hasPlatformPlan = children.some(c => c.key === 'billing.platform_plan')
      if (!hasPlatformPlan) {
        const gatewayIdx = children.findIndex(c => c.key === 'billing.gateways' || c.key === 'billing.manage_gateways')
        const insertAt = gatewayIdx >= 0 ? gatewayIdx : children.length
        const injected = [
          ...children.slice(0, insertAt),
          { key: 'billing.platform_plan', label: 'Platform Plan', icon: 'package' } as MenuItem,
          ...children.slice(insertAt),
        ]
        return { ...group, children: injected }
      }
    }

    return group
  })

  // Pass 3c: inject school.parent_portal for parent users.
  //
  // The backend assigns `school:parent` to parent user types which parseSlugsToMap
  // turns into perms['school'].canRead = true. The API /menu however returns NO
  // school group for parents — so we detect parent-only school access by:
  //   (a) perms['school.parent_portal'] or perms['my_students'] (future backend slugs, issue #551), OR
  //   (b) perms['school'].canRead = true but school group is ABSENT from the API menu
  //       AND user has no school create/update rights (distinguishes parent from staff with partial menu)
  const schoolIdxForParent = pass3b.findIndex(g => g.key === 'school')
  const hasSchoolInMenu = schoolIdxForParent >= 0
  const canParentPortal =
    perms['school.parent_portal']?.canRead === true ||
    perms['my_students']?.canRead === true ||
    // school:parent slug → canRead on 'school' but no canCreate/canUpdate → parent-only user
    (!hasSchoolInMenu &&
      perms['school']?.canRead === true &&
      !perms['school']?.canCreate &&
      !perms['school']?.canUpdate)

  let pass3c = pass3b
  if (canParentPortal) {
    const portalItem: MenuItem = { key: 'school.parent_portal', label: 'Bus Tracker', icon: 'map-pin' }
    if (hasSchoolInMenu) {
      const schoolGroup = pass3b[schoolIdxForParent]
      const alreadyHas = (schoolGroup.children ?? []).some(c => c.key === 'school.parent_portal')
      if (!alreadyHas) {
        pass3c = pass3b.map((g, i) =>
          i === schoolIdxForParent ? { ...g, children: [...(g.children ?? []), portalItem] } : g
        )
      }
    } else {
      // Parent user: API returned no school group → create a minimal one with only Bus Tracker
      pass3c = [
        ...pass3b,
        { key: 'school', label: 'School Bus', icon: 'school', children: [portalItem] },
      ]
    }
  }

  // Pass 3d: inject school.staff + school.members into the school group.
  // The backend /menu API does not return these keys yet, so we inject them
  // client-side. The presence of the school group with any non-portal item
  // (e.g. school.list) is sufficient — no separate permission check needed
  // because school admin gets 403 on the permissions endpoint, making
  // perms['school'] unreliable. If the backend later adds these keys,
  // the hasStaff/hasMembers guards prevent duplicates.
  const pass3d = pass3c.map((group) => {
    if (group.key !== 'school') return group

    const children = group.children ?? []

    // Only inject for management users — skip parent-only menus (only have school.parent_portal)
    const isManagementMenu = children.some(c => c.key === 'school.list' || c.key === 'school.students' || c.key === 'school.routes')
    if (!isManagementMenu) return group

    const hasStaff         = children.some(c => c.key === 'school.staff')
    const hasMembers       = children.some(c => c.key === 'school.members')
    const hasRuns          = children.some(c => c.key === 'school.runs')
    const hasDriver        = children.some(c => c.key === 'school.driver')
    const hasSchedule      = children.some(c => c.key === 'school.schedule')
    const hasAnnouncements = children.some(c => c.key === 'school.announcements')

    const extras: MenuItem[] = []
    if (!hasStaff)         extras.push({ key: 'school.staff',         label: 'Staff',         icon: 'users' })
    if (!hasMembers)       extras.push({ key: 'school.members',       label: 'Members',       icon: 'user-check' })
    if (!hasRuns)          extras.push({ key: 'school.runs',          label: 'Runs',          icon: 'bus' })
    if (!hasSchedule)      extras.push({ key: 'school.schedule',      label: 'Schedule',      icon: 'calendar-days' })
    if (!hasDriver)        extras.push({ key: 'school.driver',        label: 'Driver',        icon: 'truck' })
    if (!hasAnnouncements) extras.push({ key: 'school.announcements', label: 'Announcements', icon: 'megaphone' })

    const merged = extras.length ? [...children, ...extras] : children

    // Sort into canonical order: Schools → Staff → Students → Members → Routes → Attendance → Runs → Schedule → Driver → Announcements → rest
    const ORDER = [
      'school.list', 'school.staff', 'school.students', 'school.members',
      'school.routes', 'school.attendance', 'school.runs', 'school.schedule', 'school.driver',
      'school.announcements', 'school.parent_portal',
    ]
    const sorted = [...merged].sort((a, b) => {
      const ai = ORDER.indexOf(a.key)
      const bi = ORDER.indexOf(b.key)
      if (ai === -1 && bi === -1) return 0
      if (ai === -1) return 1
      if (bi === -1) return -1
      return ai - bi
    })

    return { ...group, children: sorted }
  })

  // Pass 4: cross-group route deduplication
  // Routes claimed by an earlier group are stripped from later groups.
  const claimedRoutes = new Set<string>()
  return pass3d.map((group) => ({
    ...group,
    children: (group.children ?? []).filter((child) => {
      const route = ROUTE_MAP[child.key]
      if (!route) return true            // unknown key — keep (MenuGroup handles it)
      if (claimedRoutes.has(route)) return false
      claimedRoutes.add(route)
      return true
    }),
  }))
}

// ── Group-level route fallback ─────────────────────────────────────────────────
// If a child key from the backend isn't in ROUTE_MAP, use the group's base route.
// This prevents entire menu groups from disappearing due to unknown child keys.
// Colorful icons for main menu groups
const GROUP_ICON_COLORS: Record<string, string> = {
  tracking:     '#3B82F6', // blue
  dashcam:      '#8B5CF6', // violet
  ev:           '#10B981', // emerald
  reports:      '#F59E0B', // amber
  alerts:       '#EF4444', // red
  calendars:    '#06B6D4', // cyan
  geofences:    '#F97316', // orange
  vehicles:     '#6366F1', // indigo
  drivers:      '#14B8A6', // teal
  devices:      '#64748B', // slate
  fieldforce:   '#EC4899', // pink
  dispatch:     '#8B5CF6', // violet
  school:       '#F43F5E', // rose
  transport:    '#0EA5E9', // sky
  sos:          '#EF4444', // red (emergency)
  routing:      '#22C55E', // green
  pets:         '#F472B6', // pink-400
  sims:         '#A855F7', // purple
  my_billing:   '#F59E0B', // amber
  billing:      '#059669', // emerald-600
  technician:   '#78716C', // stone
  admin:        '#475569', // slate-600
  integrations: '#7C3AED', // violet-600
  settings:     '#64748B', // slate-500
  system:       '#94A3B8', // slate-400
  whitelabel:   '#0D9488', // teal-600
  scoring:      '#D97706', // amber-600
  assistant:    '#2563EB', // blue-600
  maintenance:  '#F97316', // orange
  haul:         '#D97706', // amber-600
  haulcycles:   '#D97706', // amber-600
  haul_cycles:  '#D97706', // amber-600
}

const GROUP_ROUTE_FALLBACK: Record<string, string> = {
  reports:    '/reports',
  tracking:   '/map',
  alerts:     '/alerts',
  geofences:  '/geofences',
  vehicles:   '/vehicles',
  drivers:    '/drivers',
  devices:    '/devices',
  sims:       '/sims',
  dashcam:      '/dashcam',
  technician:   '/technician/stock',
  my_billing:   '/billing/my-billing',
  billing:      '/billing/subscriptions',
  integrations: '/admin/data-brokers',
  settings:     '/settings/me',
  calendars:    '/admin/calendars',
  fieldforce:   '/field-force/employees',
  school:     '/school/schools',
  transport:  '/transport/routes',
  sos:        '/sos/active',
  pets:       '/pets',
  batteries:  '/ev/batteries',
  ev:         '/ev/status',
  dispatch:   '/dispatch/bookings',
  routing:     '/routing',
  maintenance: '/maintenance/records',
  // admin group has no single fallback route — each item must be in ROUTE_MAP
  admin:      '/admin/users',
  system:     '/system',
  haul:        '/haul-cycles',
  haulcycles:  '/haul-cycles',
  haul_cycles: '/haul-cycles',
}

// ── Nav item component ─────────────────────────────────────────────────────────

function SidebarTooltip({ label }: { label: string }) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const ref = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    const el = ref.current?.parentElement
    if (!el) return
    const show = () => {
      const r = el.getBoundingClientRect()
      setPos({ top: r.top + r.height / 2, left: r.right + 8 })
    }
    const hide = () => setPos(null)
    el.addEventListener('mouseenter', show)
    el.addEventListener('mouseleave', hide)
    return () => { el.removeEventListener('mouseenter', show); el.removeEventListener('mouseleave', hide) }
  }, [])

  return (
    <>
      <span ref={ref} className="hidden" />
      {pos && createPortal(
        <span
          className="pointer-events-none fixed z-[9999] whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg"
          style={{ top: pos.top, left: pos.left, transform: 'translateY(-50%)' }}
        >
          {label}
        </span>,
        document.body
      )}
    </>
  )
}

function SidebarItem({
  to, label, icon: Icon, collapsed,
}: {
  to: string; label: string; icon: React.ElementType; collapsed: boolean
}) {
  const setMobileSidebarOpen = useUIStore((s) => s.setMobileSidebarOpen)
  return (
    <NavLink
      to={to}
      onClick={() => setMobileSidebarOpen(false)}
      className={({ isActive }) =>
        cn(
          'relative flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] transition-all duration-150',
          collapsed && 'justify-center hover:scale-125 hover:z-10 hover:shadow-lg hover:bg-sidebar-hover',
          isActive
            ? 'bg-primary/10 text-primary font-bold'
            : 'text-sidebar-fg font-medium hover:bg-sidebar-hover hover:text-sidebar-active-fg'
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed && <span className="truncate">{label}</span>}
      {collapsed && <SidebarTooltip label={label} />}
    </NavLink>
  )
}

// ── Menu group renderer ────────────────────────────────────────────────────────

interface InjectedItem { afterKey: string; to: string; label: string; icon: React.ElementType }

/** Translate a menu key to the i18n nav label. Falls back to API label. */
function translateMenuKey(key: string, apiLabel: string, t: (k: string) => string): string {
  // Convert "tracking.live" → "tracking_live", "alerts.list" → "alerts_list"
  const navKey = key.replace(/\./g, '_')
  const val = t(navKey)
  // t() returns the key itself when not found
  if (val !== navKey) return val

  // Try just the group key: "tracking" from "tracking.live"
  const groupKey = key.split('.')[0]
  const gVal = t(groupKey)
  if (gVal !== groupKey && !key.includes('.')) return gVal

  // Try "admin_dataBrokers" style (camelCase the part after _)
  const itemPart = key.split('.').pop() ?? ''
  const camelKey = `${groupKey}_${itemPart.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())}`
  if (camelKey !== navKey) {
    const val2 = t(camelKey)
    if (val2 !== camelKey) return val2
  }

  // Try just the item part: "list" from "billing.list"
  const itemVal = t(itemPart)
  if (itemVal !== itemPart) return itemVal

  return apiLabel
}

function MenuGroup({
  group, collapsed, inject = [], isOpen, onToggle,
}: {
  group: MenuItem
  collapsed: boolean
  inject?: InjectedItem[]
  isOpen: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation('nav')
  const groupRef = useRef<HTMLDivElement>(null)

  // Scroll group into view when it opens so sub-items aren't hidden below the fold
  useEffect(() => {
    if (isOpen && groupRef.current) {
      // Small delay to let the DOM expand first
      const t = setTimeout(() => {
        groupRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }, 60)
      return () => clearTimeout(t)
    }
  }, [isOpen])

  const children = group.children ?? []
  if (children.length === 0) return null

  // De-duplicate children by route; fall back to group-level route for unknown keys
  const seen = new Set<string>()
  const unique = children.filter((child) => {
    const route = ROUTE_MAP[child.key] ?? GROUP_ROUTE_FALLBACK[group.key]
    if (!route || seen.has(route)) return false
    seen.add(route)
    return true
  })
  if (unique.length === 0) return null

  // Build final list, inserting injected items after their anchor key
  const items: { key: string; to: string; label: string; icon: React.ElementType }[] = []
  for (const child of unique) {
    items.push({ key: child.key, to: ROUTE_MAP[child.key] ?? GROUP_ROUTE_FALLBACK[group.key] ?? '/', label: translateMenuKey(child.key, child.label, t), icon: resolveIcon(child.icon) })
    for (const inj of inject) {
      if (inj.afterKey === child.key && !items.some((i) => i.to === inj.to)) {
        items.push({ key: `__inject_${inj.to}`, to: inj.to, label: inj.label, icon: inj.icon })
      }
    }
  }

  // Icon-only (collapsed sidebar) — show divider + items, no accordion header
  if (collapsed) {
    return (
      <>
        <div className="my-1.5 border-t border-border" />
        {items.map((item) => (
          <SidebarItem key={item.key} to={item.to} label={item.label} icon={item.icon} collapsed={collapsed} />
        ))}
      </>
    )
  }

  // Full sidebar — accordion group
  const GroupIcon = resolveIcon(group.icon ?? '')
  return (
    <div ref={groupRef} className="mt-1">
      <button
        onClick={onToggle}
        className={cn(
          'flex w-full items-center justify-between rounded-lg px-3 py-2 transition-colors',
          isOpen
            ? 'text-sidebar-active-fg'
            : 'text-muted hover:text-sidebar-active-fg hover:bg-sidebar-hover'
        )}
      >
        <span className="flex items-center gap-2">
          <GroupIcon className="h-3.5 w-3.5 shrink-0" style={{ color: GROUP_ICON_COLORS[group.key] }} />
          <span className="text-xs font-bold uppercase tracking-wider">{translateMenuKey(group.key, group.label, t)}</span>
        </span>
        {isOpen
          ? <ChevronDown className="h-3.5 w-3.5 shrink-0" />
          : <ChevronRight className="h-3.5 w-3.5 shrink-0" />
        }
      </button>
      {isOpen && (
        <div className="mt-0.5 space-y-0.5 pl-4">
          {items.map((item) => (
            <SidebarItem key={item.key} to={item.to} label={item.label} icon={item.icon} collapsed={collapsed} />
          ))}
        </div>
      )}
    </div>
  )
}

// ── Fallback menu builder (used when /menu returns empty — backend bug) ────────

function buildFallbackMenu(perms: PermissionMap, isSuperAdmin: boolean, isOwner = false, modules: string[] = [], isAdmin = false): MenuItem[] {
  const can = (r: string) => isSuperAdmin || perms[r]?.canRead === true
  // Mirror useModule() logic: super-admin and empty-modules (loading / open domain) always pass
  const hasMod = (slug: string) => isSuperAdmin || modules.length === 0 || modules.includes(slug)
  const item = (key: string, label: string, icon: string): MenuItem => ({ key, label, icon })
  const group = (key: string, label: string, icon: string, children: MenuItem[]): MenuItem =>
    ({ key, label, icon, children })

  const groups: MenuItem[] = []

  // Tracking — only Live map stays here
  const trackingItems: MenuItem[] = []
  if (can('tracking') || can('vehicles')) trackingItems.push(item('tracking.live',    'Live',              'map-pin'))
  if (can('devices') || can('tracking'))  trackingItems.push(item('tracking.packets', 'Packet Inspector',  'terminal'))
  if (trackingItems.length) groups.push(group('tracking', 'Tracking', 'map-pin', trackingItems))

  // Dashcam — after Tracking
  if (can('dashcam') && hasMod('dashcam')) {
    const dashcamItems: MenuItem[] = []
    dashcamItems.push(item('dashcam.live',        'Live View',   'video'))
    dashcamItems.push(item('dashcam.recordings',  'Recordings',  'download'))
    dashcamItems.push(item('dashcam.alarm_clips', 'Alarm Clips', 'bell'))
    dashcamItems.push(item('dashcam.config',      'Config',      'settings'))
    groups.push(group('dashcam', 'Dashcam', 'video', dashcamItems))
  }

  // Alerts (+ Alert Rules — now in the Alerts group per catalogue v2, not Admin)
  if (can('alerts') || can('alert_rules')) {
    const alertItems: MenuItem[] = []
    if (can('alerts'))      alertItems.push(item('alerts.list',  'Alerts',      'bell'))
    if (can('alert_rules') || isSuperAdmin) alertItems.push(item('alerts.rules', 'Alert Rules', 'bell'))
    groups.push(group('alerts', 'Alerts', 'bell', alertItems))
  }
  // Geofences
  if (can('geofences')) groups.push(group('geofences', 'Geofences', 'pentagon', [item('geofences.list', 'Geofences', 'pentagon')]))
  // Vehicles
  if (can('vehicles'))  groups.push(group('vehicles',  'Vehicles',  'truck',   [item('vehicles.list', 'Vehicles', 'truck'), item('vehicles.types', 'Vehicle Types', 'layers')]))
  // Drivers
  if (can('drivers'))   groups.push(group('drivers',   'Drivers',   'user-check', [item('drivers.list', 'Drivers', 'user-check')]))
  // Devices
  if (can('devices'))   groups.push(group('devices',   'Devices',   'cpu',    [item('devices.list', 'Devices', 'cpu')]))
  // SIMs
  if (can('sims'))      groups.push(group('sims',      'SIM Cards', 'sim-card', [item('sims.list', 'SIM Cards', 'sim-card')]))

  // My Billing — owner self-service, only for users WITHOUT full admin billing access.
  // billing:view is shared between owner and admin, so we must explicitly exclude
  // users who also have admin billing features (they get the full BILLING group below).
  const hasAdminBilling = can('billing') || can('billing_subscriptions') || can('billing_plans') || can('billing_invoices')
  if (!isSuperAdmin && !hasAdminBilling && can('my_billing')) {
    groups.push(group('my_billing', 'My Billing', 'credit-card', [
      item('my_billing.subscription', 'Subscription', 'receipt'),
      item('my_billing.invoices',     'Invoices',     'file-text'),
      item('my_billing.payments',     'Payments',     'credit-card'),
    ]))
  }

  // Billing
  if (hasAdminBilling) {
    const billingItems: MenuItem[] = []
    // Dashboard — admin and super-admin only
    if (isSuperAdmin || isAdmin) billingItems.push(item('billing.dashboard', 'Billing Dashboard', 'bar-chart-2'))
    // Domain admin features
    if (!isSuperAdmin) {
      billingItems.push(item('billing.subscriptions',  'Subscriptions',       'receipt'))
      billingItems.push(item('billing.customers',      'Customer Billing',    'users'))
      // Invoices are in the Customer Billing tabs for owner users — no standalone nav item
      if (!isOwner) billingItems.push(item('billing.invoices', 'Invoices', 'file-text'))
      // billing.payments is accessible via the Payments tab inside Customer Billing — no standalone nav item
      billingItems.push(item('billing.domain_plans',   'Domain Plans',        'layers'))
      billingItems.push(item('billing.platform_plan',  'Platform Plan',       'package'))
      billingItems.push(item('billing.gateways',       'Payment Gateways',    'credit-card'))
    }
    // Super admin
    if (isSuperAdmin) {
      billingItems.push(item('billing.invoices',           'Invoices',           'file-text'))
      billingItems.push(item('billing.platform_gateways',  'Payment Gateways',   'credit-card'))
    }
    billingItems.push(item('billing.profiles', 'Profile', 'building'))
    groups.push(group('billing', 'Billing', 'credit-card', billingItems))
  }

  // Technician
  if (can('technician') && hasMod('technician')) {
    groups.push(group('technician', 'Technician', 'tool', [
      item('technician.stock',    'Stock',    'package'),
      item('technician.activate', 'Activate', 'zap'),
      item('technician.handover', 'Handover', 'share-2'),
    ]))
  }

  // Field Force
  if (can('fieldforce') && hasMod('fieldforce')) {
    groups.push(group('fieldforce', 'Field Force', 'briefcase', [
      item('fieldforce.list',       'Employees',  'users'),
      item('fieldforce.tasks',      'Tasks',      'check-square'),
      item('fieldforce.attendance', 'Attendance', 'calendar'),
    ]))
  }

  // School — distinguish admin/staff from parent-only users.
  // school:parent slug → canRead=true but no canCreate/canUpdate on 'school'.
  // school:manage / school:read (staff) → canCreate or canUpdate also set.
  const schoolCanManage = isSuperAdmin || perms['school']?.canCreate === true || perms['school']?.canUpdate === true
  const schoolCanRead   = can('school')  // any school:* slug
  const schoolParentOnly = schoolCanRead && !schoolCanManage  // parent-only: read only via school:parent

  if (schoolCanManage && hasMod('school')) {
    // School admin / staff — show full management menu
    const schoolItems: MenuItem[] = [
      item('school.list',       'Schools',    'school'),
      item('school.staff',      'Staff',      'users'),
      item('school.students',   'Students',   'users'),
      item('school.members',    'Members',    'user-check'),
      item('school.routes',     'Routes',     'route'),
      item('school.attendance', 'Attendance', 'calendar'),
      item('school.runs',       'Runs',       'bus'),
      item('school.schedule',   'Schedule',   'calendar-days'),
      item('school.driver',     'Driver',     'truck'),
    ]
    if (can('school.parent_portal') || can('my_students') || schoolParentOnly) {
      schoolItems.push(item('school.parent_portal', 'Bus Tracker', 'map-pin'))
    }
    schoolItems.push(item('admin.announcements', 'Announcements', 'megaphone'))
    groups.push(group('school', 'School Bus', 'school', schoolItems))
  } else if ((schoolParentOnly || can('school.parent_portal') || can('my_students')) && hasMod('school')) {
    // Parent users: only Bus Tracker
    groups.push(group('school', 'School Bus', 'school', [
      item('school.parent_portal', 'Bus Tracker', 'map-pin'),
    ]))
  }

  // Transport
  if (can('transport') && hasMod('transport')) {
    groups.push(group('transport', 'Transport', 'bus', [
      item('transport.vendors',   'Vendors',   'building'),
      item('transport.employees', 'Employees', 'users'),
      item('transport.list',      'Routes',    'route'),
      item('transport.roster',    'Rosters',   'list'),
      item('transport.sos',       'SOS',       'bell'),
      item('transport.vrp',       'VRP',       'route'),
    ]))
  }

  // Pets
  if (can('pets') && hasMod('pets')) groups.push(group('pets', 'Pets', 'paw-print', [item('pets.list', 'Pets', 'paw-print')]))

  // EV (new catalogue v2 keys from issue #57)
  if ((can('ev') || can('batteries')) && hasMod('ev')) {
    const evItems: MenuItem[] = []
    const canEvStations = isSuperAdmin || perms['ev']?.canCreate === true
    if (can('ev'))        { evItems.push(item('ev.status',            'EV Status',          'battery')) }
    if (can('ev'))        { evItems.push(item('ev.history',           'EV History',         'activity')) }
    if (can('ev'))        { evItems.push(item('ev.charging_sessions', 'Charging Sessions',  'zap')) }
    if (canEvStations)    { evItems.push(item('ev.stations',          'EV Stations',        'zap')) }
    // Legacy batteries (kept for deployments still on old catalogue)
    if (can('batteries')) { evItems.push(item('batteries.list', 'Batteries', 'battery')); evItems.push(item('batteries.swap', 'Swaps', 'repeat')) }
    if (evItems.length)   groups.push(group('ev', 'EV', 'battery-charging', evItems))
  }

  // Dispatch
  if (can('dispatch') && hasMod('dispatch')) {
    groups.push(group('dispatch', 'Dispatch', 'send', [
      item('dispatch.list',     'Bookings',      'send'),
      item('dispatch.services', 'Service Types', 'settings'),
      item('dispatch.packages', 'Packages',      'package'),
    ]))
  }

  // AI Assistant
  if ((can('assistant') || isSuperAdmin) && hasMod('ai_assistant')) {
    groups.push(group('assistant', 'AI Assistant', 'message-circle', [
      item('assistant.chat', 'Chat', 'message-circle'),
    ]))
  }

  // Routing / Route Planning
  if ((can('routing') || isSuperAdmin) && hasMod('routing')) {
    groups.push(group('routing', 'Route Planning', 'route', [
      item('routing.plans', 'Route Plans', 'route'),
    ]))
  }

  // Maintenance
  if (can('maintenance') && hasMod('maintenance')) {
    groups.push(group('maintenance', 'Maintenance', 'wrench', [
      item('maintenance.records',   'Service Records',   'wrench'),
      item('maintenance.schedules', 'Service Schedules', 'calendar'),
    ]))
  }

  // Reports — GPS History + Trips + On-Road Summary
  const reportItems: MenuItem[] = []
  if (can('tracking') || can('vehicles'))  reportItems.push(item('tracking.history', 'GPS History',     'clock'))
  if (can('trips')    || can('tracking'))  reportItems.push(item('tracking.trips',   'Trips',           'route'))
  if (can('reports')  || can('report') || can('reporting'))
                                           reportItems.push(item('reports.list',     'On-Road Summary',  'bar-chart'))
  if (reportItems.length) groups.push(group('reports', 'Reports', 'bar-chart', reportItems))

  // Administration
  const adminItems: MenuItem[] = []
  if (can('users')              || isSuperAdmin) adminItems.push(item('admin.users',        'Users',        'users'))
  if (can('user_types')         || isSuperAdmin) adminItems.push(item('admin.user_types',   'User Types',   'shield'))
  if (can('databroker_configs') || isSuperAdmin) adminItems.push(item('admin.data_brokers', 'Data Brokers', 'share-2'))
  if (can('calendars')   || isSuperAdmin) adminItems.push(item('admin.calendars', 'Calendars', 'calendar'))
  if (can('assistant')   || isSuperAdmin) adminItems.push(item('admin.assistant_config', 'AI Config', 'message-circle'))
  if (isSuperAdmin) {
    // dashcam.config is already in the dashcam group — only add doc reminders here
    adminItems.push(item('admin.doc_reminders',   'Doc Reminders',        'bell'))
  }
  // API Keys: requires api_keys:manage permission (previously was super-admin only)
  if (isSuperAdmin || can('api_keys')) adminItems.push(item('admin.api_keys', 'API Keys', 'key'))
  if (isSuperAdmin || can('audit_log') || can('admin.audit_log')) adminItems.push(item('admin.audit_log', 'Audit Log', 'clipboard-list'))
  if (isSuperAdmin || isAdmin || can('users')) adminItems.push(item('admin.delegations', 'Delegations', 'user-check'))
  if (isSuperAdmin || isAdmin || can('announcements') || can('announcements.send')) adminItems.push(item('admin.announcements', 'Announcements', 'megaphone'))
  if (adminItems.length) groups.push(group('admin', 'Administration', 'settings', adminItems))

  // Billing extras — visible to domain admins with billing perm (not just super-admin)
  // Tax Config, PDF Templates, Signing Cert, and Dunning Rules are domain-scoped features
  if (isSuperAdmin || can('billing') || can('billing.tax_config') || can('billing.invoices')) {
    const billingGroup = groups.find((g) => g.key === 'billing')
    if (billingGroup) {
      billingGroup.children = billingGroup.children ?? []
      billingGroup.children.push(item('billing.tax_config',   'Tax Config',        'shield'))
      // Domain admins see "Invoice Templates"; super admin gets "Platform PDF Templates" below
      if (!isSuperAdmin) billingGroup.children.push(item('billing.pdf_templates', 'Invoice Templates', 'file-text'))
      billingGroup.children.push(item('billing.signing_cert', 'Signing Cert',      'award'))
      billingGroup.children.push(item('billing.exports',      'GST Exports',       'download'))
      billingGroup.children.push(item('billing.dunning_rules','Payment Reminders', 'bell'))
    }
  }

  // Super-admin-only billing items
  if (isSuperAdmin) {
    const billingGroup = groups.find((g) => g.key === 'billing')
    if (billingGroup) {
      billingGroup.children = billingGroup.children ?? []
      // Insert Platform PDF Templates after Tax Config
      const taxIdx = billingGroup.children.findIndex((c) => c.key === 'billing.tax_config')
      const pdfItem = item('billing.platform_pdf_templates', 'Platform PDF Templates', 'file-text')
      if (taxIdx >= 0) billingGroup.children.splice(taxIdx + 1, 0, pdfItem)
      else billingGroup.children.push(pdfItem)
      billingGroup.children.push(item('billing.platform_profile', 'Platform Profile', 'building'))
    }
  }

  return groups
}

// ── Main Sidebar ───────────────────────────────────────────────────────────────

export function Sidebar() {
  const { t } = useTranslation('nav')
  const collapsed = useUIStore((s) => s.sidebarCollapsed)
  const toggleSidebar = useUIStore((s) => s.toggleSidebar)
  const mobileSidebarOpen = useUIStore((s) => s.mobileSidebarOpen)
  const setMobileSidebarOpen = useUIStore((s) => s.setMobileSidebarOpen)
  const theme = useTenantStore((s) => s.theme)
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const is_super = useAuthStore((s) => s.is_super)
  const is_owner = useAuthStore((s) => s.is_owner)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const permMap = useAuthStore((s) => s.permissions)
  const permissionsLoaded = useAuthStore((s) => s.permissionsLoaded)
  const modules = useAuthStore((s) => s.modules)
  const { data: apiVersion } = useQuery({
    queryKey: ['api-version'],
    queryFn: getApiVersion,
    staleTime: Infinity,
    retry: false,
  })
  const navRef = useRef<HTMLElement>(null)
  const { pathname } = useLocation()
  const [openGroup, setOpenGroup] = useState<string | null>(null)

  const { data: menuGroups = [], isLoading } = useQuery({
    queryKey: ['menu'],
    queryFn: getMenu,
    staleTime: 60_000,
    enabled: isAuthenticated,
  })

  // The /menu API returns a fully-filtered tree for the current user — no
  // client-side rebuilding needed. postProcessMenu only applies label overrides
  // and moves tracking.history/trips into the Reports group (UX convention).
  // If the API returns empty (e.g. first load before permissions propagate),
  // buildFallbackMenu() is used as a safety net.
  const is_admin = user?.is_admin ?? false

  const effectiveGroups = useMemo(() => {
    if (menuGroups.length > 0) {
      return postProcessMenu(menuGroups, is_super, permMap)
    }
    if (isLoading || !permissionsLoaded) return []
    // Fallback only when menu API returns empty
    return buildFallbackMenu(permMap, is_super, is_owner, modules, is_admin)
  }, [menuGroups, isLoading, permissionsLoaded, permMap, is_super, is_owner, is_admin, modules])

  // Auto-open the group whose child route matches the current pathname
  useEffect(() => {
    if (!effectiveGroups.length) return
    for (const group of effectiveGroups) {
      const children = group.children ?? []
      const hasActiveChild = children.some((child) => {
        const route = ROUTE_MAP[child.key] ?? GROUP_ROUTE_FALLBACK[group.key]
        return route && (pathname === route || pathname.startsWith(route + '/'))
      })
      if (hasActiveChild) {
        setOpenGroup(group.key)
        return
      }
    }
  }, [pathname, effectiveGroups])

  // Scroll active nav item into view whenever the route or menu changes
  useEffect(() => {
    const nav = navRef.current
    if (!nav) return
    const active = nav.querySelector<HTMLElement>('[aria-current="page"]')
    if (active) active.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [pathname, effectiveGroups])

  return (
    <aside
      className={cn(
        'fixed inset-y-0 start-0 z-[900] flex flex-col bg-sidebar border-e border-border transition-all duration-200',
        // Mobile: always full width, slide in/out based on mobileSidebarOpen
        'max-lg:w-64',
        mobileSidebarOpen
          ? 'max-lg:translate-x-0'
          : 'ltr:max-lg:-translate-x-full rtl:max-lg:translate-x-full',
        // Desktop: width based on collapsed state, always visible
        'lg:translate-x-0',
        collapsed ? 'lg:w-16' : 'lg:w-64',
      )}
    >
      {/* Logo area */}
      <div className="flex h-14 shrink-0 items-center gap-3 border-b border-border px-4">
        {theme?.logoUrl ? (
          <img src={theme.logoUrl} alt={theme.appName} className="h-8 w-auto object-contain" />
        ) : (
          <img src={`/icons/domain-logos/${window.location.hostname}.jpeg`} alt="" className="h-8 w-8 shrink-0 rounded object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }} />
        )}
        {!collapsed && (
          <span className="truncate text-sm font-semibold text-sidebar-active-fg">
            {theme?.appName || 'Veytra Nxt'}
          </span>
        )}
      </div>

      {/* Navigation */}
      <nav ref={navRef} className="flex-1 space-y-0.5 overflow-y-auto p-2">
        {/* Dashboard — always visible */}
        <SidebarItem
          to="/dashboard"
          label={t('dashboard')}
          icon={LayoutDashboard}
          collapsed={collapsed}
        />

        {/* Dynamic menu — fully driven by /menu API + permission-based fallback */}
        {isLoading ? (
          !collapsed && (
            <div className="mt-4 space-y-2 px-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="skeleton h-7 rounded" />
              ))}
            </div>
          )
        ) : (
          effectiveGroups.map((group) => (
            <MenuGroup
              key={group.key}
              group={group}
              collapsed={collapsed}
              isOpen={openGroup === group.key}
              onToggle={() => setOpenGroup(prev => prev === group.key ? null : group.key)}
            />
          ))
        )}

        {/* White Label comes from API as admin.domains (gated by domains:manage for super admin) */}
      </nav>

      {/* User footer */}
      <div className="shrink-0 border-t border-border p-2">
        <NavLink
          to="/settings/me"
          onClick={() => setMobileSidebarOpen(false)}
          className={({ isActive }) =>
            cn(
              'relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all duration-150',
              collapsed && 'justify-center hover:scale-125 hover:z-10 hover:shadow-lg hover:bg-sidebar-hover',
              isActive
                ? 'bg-primary/10 text-primary font-semibold'
                : 'text-sidebar-fg hover:bg-sidebar-hover hover:text-sidebar-active-fg'
            )
          }
        >
          <User2 className="h-4 w-4 shrink-0" />
          {!collapsed && (
            <span className="flex-1 truncate">{user?.name ?? t('profile')}</span>
          )}
          {collapsed && <SidebarTooltip label={user?.name ?? t('profile')} />}
        </NavLink>

        <button
          onClick={() => { setMobileSidebarOpen(false); logout() }}
          className="mt-0.5 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-sidebar-fg transition-colors hover:bg-sidebar-hover hover:text-sidebar-active-fg"
          title={collapsed ? t('logout') : undefined}
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {!collapsed && (
            <div className="flex flex-1 items-center justify-between gap-1 min-w-0">
              <span className="truncate">{t('logout')}</span>
              {apiVersion && (
                <span
                  className="shrink-0 text-[9px] text-muted/60 tabular-nums"
                  title={`Build: ${apiVersion.commit} · ${new Date(apiVersion.build_date).toLocaleDateString()}`}
                >
                  ({apiVersion.version.replace(/-unknown$/, '')}
                  {apiVersion.version.endsWith('-unknown') && <span className="text-amber-500 font-semibold"> dev</span>})
                </span>
              )}
            </div>
          )}
        </button>
      </div>

      {/* Collapse toggle — desktop only */}
      <button
        onClick={toggleSidebar}
        className="absolute -end-3 top-20 hidden h-6 w-6 items-center justify-center rounded-full border border-border bg-surface text-muted shadow-sm hover:text-primary lg:flex"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        <ChevronRight className={cn('h-3 w-3 transition-transform', !collapsed && 'rotate-180')} />
      </button>
    </aside>
  )
}
